import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { formatWebFeedDistanceLabel } from '@/features/events/feed/feedFormatters';
import type { FeedDealItem } from '@/lib/api/apiTypes';
import { DealTimingStateLive } from '@/lib/api/apiTypes';
import { trackDealTap } from '@/lib/analytics/posthog';
import { getVenueHref } from '@/lib/navigation/routes';
import {
  getVenueDealCategoryColor,
  getVenueDealCategoryLabel,
} from '@radious/catalog/deal-categories';
import { router } from 'expo-router';
import { BookmarkIcon, MapPinIcon } from 'lucide-react-native';
import { Pressable, View } from 'react-native';

export function WebFeedDealCard({
  compact,
  fallbackIndex,
  item,
  onDirections,
  onSave,
  onShare,
}: {
  compact: boolean;
  fallbackIndex: number;
  item: FeedDealItem;
  onDirections: (item: FeedDealItem) => void;
  onSave: (item: FeedDealItem) => void;
  onShare: (item: FeedDealItem) => void;
}) {
  const isLive = item.timing_state === DealTimingStateLive;
  const isSaved = item.deal.is_saved;
  const categoryColor = getDealCategoryAccent(item.deal.category);
  const venueHref = getVenueHref(
    item.deal.venue_id,
    { deal: item.deal.id, source: 'web_feed' },
    { name: item.deal.venue_name }
  );
  const distanceLabel = formatWebFeedDistanceLabel(item.deal.distance_meters);
  const description = item.deal.description.trim();

  return (
    <Pressable
      onPress={() => {
        trackDealTap({
          category: item.deal.category,
          dealID: item.deal.id,
          source: 'feed',
          surface: 'web',
          timingState: item.timing_state,
          venueID: item.deal.venue_id,
        });
        router.push(venueHref as any);
      }}
      style={
        {
          overflow: 'hidden',
          borderRadius: 20,
          borderWidth: 1,
          borderColor: withCSSOpacity(categoryColor, 0.2),
          backgroundColor: '#111009',
          gridColumn: compact ? undefined : '1 / -1',
        } as any
      }>

      {/* Card content */}
      <View style={{ flex: 1, padding: 16, gap: 10 }}>
        {/* Badges row */}
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 5 }}>
          <View
            style={{
              borderRadius: 999,
              borderWidth: 1,
              paddingHorizontal: 7,
              paddingVertical: 2,
              backgroundColor: withCSSOpacity(categoryColor, 0.15),
              borderColor: withCSSOpacity(categoryColor, 0.32),
            }}>
            <Text
              style={{
                fontSize: 10,
                fontFamily: 'Outfit_700Bold',
                letterSpacing: 0.8,
                textTransform: 'uppercase',
                color: categoryColor,
              }}>
              {getDealCategoryLabel(item.deal.category)}
            </Text>
          </View>
          {isLive ? (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 4,
                borderRadius: 999,
                borderWidth: 1,
                borderColor: 'rgba(239,78,46,0.35)',
                backgroundColor: 'rgba(239,78,46,0.12)',
                paddingHorizontal: 7,
                paddingVertical: 2,
              }}>
              <View style={{ width: 5, height: 5, borderRadius: 2.5, backgroundColor: '#ef4e2e' }} />
              <Text
                style={{
                  fontSize: 10,
                  fontFamily: 'Outfit_700Bold',
                  letterSpacing: 0.8,
                  textTransform: 'uppercase',
                  color: '#ef4e2e',
                }}>
                Live now
              </Text>
            </View>
          ) : null}
          <View
            style={{
              borderRadius: 999,
              backgroundColor: 'rgba(255,255,255,0.07)',
              paddingHorizontal: 7,
              paddingVertical: 2,
            }}>
            <Text
              style={{ fontSize: 10, fontFamily: 'Outfit_600SemiBold', color: 'rgba(240,236,228,0.5)' }}
              numberOfLines={1}>
              {item.timing_label}
            </Text>
          </View>
        </View>

        {/* Title + description */}
        <View style={{ gap: 3 }}>
          <Text
            style={{
              fontSize: 15,
              fontFamily: 'Outfit_700Bold',
              color: 'rgba(240,236,228,0.96)',
              lineHeight: 20,
            }}
            numberOfLines={1}>
            {item.deal.title}
          </Text>
          {description ? (
            <Text
              style={{
                fontSize: 12,
                fontFamily: 'Outfit_400Regular',
                lineHeight: 17,
                color: 'rgba(240,236,228,0.55)',
              }}
              numberOfLines={2}>
              {description}
            </Text>
          ) : null}
        </View>

        {/* Venue row */}
        <View style={{ flexDirection: 'row', alignItems: 'center', minWidth: 0 }}>
          <Icon as={MapPinIcon} size={11} style={{ color: '#ef4e2e', marginRight: 3 }} />
          <Text
            style={{ fontSize: 11, fontFamily: 'Outfit_600SemiBold', color: 'rgba(240,236,228,0.65)' }}
            numberOfLines={1}>
            {item.deal.venue_name}
          </Text>
          {distanceLabel ? (
            <>
              <View
                style={{
                  width: 3,
                  height: 3,
                  borderRadius: 1.5,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  marginHorizontal: 5,
                }}
              />
              <Text
                style={{ fontSize: 11, color: 'rgba(240,236,228,0.4)' }}
                numberOfLines={1}>
                {distanceLabel}
              </Text>
            </>
          ) : null}
        </View>

        {/* Action buttons */}
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
          <Pressable
            style={{
              borderRadius: 999,
              backgroundColor: '#ef4e2e',
              paddingHorizontal: 12,
              paddingVertical: 6,
            }}
            onPress={(e) => {
              e?.stopPropagation?.();
              router.push(venueHref as any);
            }}>
            <Text style={{ fontSize: 11, fontFamily: 'Outfit_700Bold', color: '#fff' }}>
              Open deal
            </Text>
          </Pressable>
          <Pressable
            style={{
              borderRadius: 999,
              backgroundColor: 'rgba(255,255,255,0.07)',
              paddingHorizontal: 11,
              paddingVertical: 6,
            }}
            onPress={(e) => {
              e?.stopPropagation?.();
              onDirections(item);
            }}>
            <Text style={{ fontSize: 11, fontFamily: 'Outfit_600SemiBold', color: 'rgba(240,236,228,0.6)' }}>
              Directions
            </Text>
          </Pressable>
          <Pressable
            style={{
              borderRadius: 999,
              backgroundColor: 'rgba(255,255,255,0.07)',
              paddingHorizontal: 11,
              paddingVertical: 6,
            }}
            onPress={(e) => {
              e?.stopPropagation?.();
              onShare(item);
            }}>
            <Text style={{ fontSize: 11, fontFamily: 'Outfit_600SemiBold', color: 'rgba(240,236,228,0.6)' }}>
              Share
            </Text>
          </Pressable>
        </View>
      </View>

      {/* Save button */}
      <Pressable
        onPress={(e) => {
          e?.stopPropagation?.();
          onSave(item);
        }}
        style={{
          position: 'absolute',
          top: 14,
          right: 14,
          width: 30,
          height: 30,
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 15,
          backgroundColor: isSaved ? 'rgba(239,78,46,0.20)' : 'rgba(0,0,0,0.45)',
          borderWidth: 1,
          borderColor: isSaved ? 'rgba(239,78,46,0.40)' : 'rgba(255,255,255,0.10)',
        }}>
        <Icon
          as={BookmarkIcon}
          size={13}
          color={isSaved ? 'rgb(239,78,46)' : 'rgba(240,236,228,0.72)'}
          {...({ fill: isSaved ? 'rgb(239,78,46)' : 'transparent' } as any)}
        />
      </Pressable>
    </Pressable>
  );
}

function getDealCategoryAccent(category: FeedDealItem['deal']['category']) {
  return getVenueDealCategoryColor(category);
}

function getDealCategoryLabel(value: string) {
  return getVenueDealCategoryLabel(value);
}

function withCSSOpacity(color: string, opacity: number) {
  if (color.startsWith('rgba(')) {
    return color.replace(/rgba\(([^,]+),([^,]+),([^,]+),([^)]+)\)/, (_match, r, g, b) => {
      return `rgba(${r.trim()},${g.trim()},${b.trim()},${opacity})`;
    });
  }

  if (color.startsWith('#') && color.length === 7) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${opacity})`;
  }

  return color;
}

